import { useRecoilValue } from "recoil";
import {
  cloverDeltaUpdatesState,
  designUsingCloverState, designUsingPopmenuState,
  designUsingSquareState,
  designUsingToastState, designUsingTreezState, popmenuDeltaUpdatesState,
  posState,
  squareDeltaUpdatesState,
  toastDeltaUpdatesState, treezDeltaUpdatesState
} from "../state/atoms";
import "./MenuDataSourceBanner.scss";
import { NavLink } from "react-router-dom";
import { env } from "../env/env";
import {useEffect, useMemo, useState} from "react";
import {getUpdateItemCount} from "../service/PosService";
import SquareIcon from "./icons/SquareIcon";
import CloverLogo from "./icons/CloverLogo";
import TreezLogo from "./icons/TreezLogo";
import PopmenuLogo from "./icons/PopmenuLogo";

interface MenuDataSourceBannerProps {
  isUnverifiedToastUser: boolean;
}

export default function MenuDataSourceBanner({ isUnverifiedToastUser } : MenuDataSourceBannerProps) {
  const pos = useRecoilValue(posState);
  const toastDeltaUpdates = useRecoilValue(toastDeltaUpdatesState);
  const squareDeltaUpdates = useRecoilValue(squareDeltaUpdatesState);
  const cloverDeltaUpdates = useRecoilValue(cloverDeltaUpdatesState);
  const treezDeltaUpdates = useRecoilValue(treezDeltaUpdatesState);
  const popmenuDeltaUpdates = useRecoilValue(popmenuDeltaUpdatesState);
  const [unresolvedUpdates, setUnresolvedUpdates] = useState(0);
  const designsUsingToast = useRecoilValue(designUsingToastState);
  const designsUsingClover = useRecoilValue(designUsingCloverState);
  const designsUsingSquare = useRecoilValue(designUsingSquareState);
  const designsUsingTreez = useRecoilValue(designUsingTreezState);
  const designsUsingPopmenu = useRecoilValue(designUsingPopmenuState);

  useEffect(() => {
    if (pos.isToast) {
      setUnresolvedUpdates(designsUsingToast.map(design => {
        if (toastDeltaUpdates[design.id!] && !toastDeltaUpdates[design.id!]?.solved) {
          return getUpdateItemCount(toastDeltaUpdates, design.id!);
        }
        return 0;
      }).reduce((a, b) => a + b, 0));
    } else if (pos.isSquare) {
      setUnresolvedUpdates(designsUsingSquare.map(design => {
        if (squareDeltaUpdates[design.id!] && !squareDeltaUpdates[design.id!]?.solved) {
          return getUpdateItemCount(squareDeltaUpdates, design.id!);
        }
        return 0;
      }).reduce((a, b) => a + b, 0));
    } else if (pos.isTreez) {
      setUnresolvedUpdates(designsUsingTreez.map(design => {
        if (treezDeltaUpdates[design.id!] && !treezDeltaUpdates[design.id!]?.solved) {
          return getUpdateItemCount(treezDeltaUpdates, design.id!);
        }
        return 0;
      }).reduce((a, b) => a + b, 0));
    } else if (pos.isPopmenu) {
      setUnresolvedUpdates(designsUsingPopmenu.map(design => {
        if (popmenuDeltaUpdates[design.id!] && !popmenuDeltaUpdates[design.id!]?.solved) {
          return getUpdateItemCount(popmenuDeltaUpdates, design.id!);
        }
        return 0;
      }).reduce((a, b) => a + b, 0));
    } else {
      setUnresolvedUpdates(designsUsingClover.map(design => {
        if (cloverDeltaUpdates[design.id!] && !cloverDeltaUpdates[design.id!]?.solved) {
          return getUpdateItemCount(cloverDeltaUpdates, design.id!);
        }
        return 0;
      }).reduce((a, b) => a + b, 0));
    }
  }, [designsUsingToast, designsUsingClover, designsUsingSquare, designsUsingTreez, designsUsingPopmenu, toastDeltaUpdates, squareDeltaUpdates, cloverDeltaUpdates, treezDeltaUpdates, popmenuDeltaUpdates, pos.isToast, pos.isSquare, pos.isTreez, pos.isPopmenu]);

  const icon = useMemo(() => {
    if (pos.isToast || isUnverifiedToastUser) {
      return <img src="https://timber.mhmcdn.com/site/marketing/icons/Toast.svg" alt="Toast"/>;
    } else if (pos.isSquare) {
      return <SquareIcon/>;
    } else if (pos.isTreez) {
      return <TreezLogo/>;
    } else if (pos.isPopmenu) {
      return <PopmenuLogo/>;
    } else {
      return <CloverLogo/>;
    }
  }, [pos.isSquare, pos.isToast, pos.isTreez, pos.isPopmenu, isUnverifiedToastUser]);

  const source = useMemo(() => {
    if (pos.isToast || isUnverifiedToastUser) {
      return 'Toast';
    } else if (pos.isSquare) {
      return 'Square';
    } else if (pos.isTreez) {
      return 'Treez';
    } else if (pos.isPopmenu) {
      return 'Popmenu';
    } else {
      return 'Clover';
    }
  }, [pos.isToast, pos.isSquare, pos.isTreez, pos.isPopmenu, isUnverifiedToastUser]);

  return (
    <NavLink to={env.dashboardPath + "/design-updater"} className={"menu-data-source-banner"}>
      {<>
        <div>
          {icon}
          {!isUnverifiedToastUser && <div>
            Menu data source: {source}
            {unresolvedUpdates === 0 && <span className="auto-span">Item updates set to auto</span>}
            {unresolvedUpdates > 0 && <span>{unresolvedUpdates} {unresolvedUpdates === 1 ? "Update" : "Updates"} found</span>}
          </div>}
          {isUnverifiedToastUser &&
          <div>Enjoy the benefits of integration.</div>}
        </div>
        {unresolvedUpdates > 0 && <NavLink to={env.dashboardPath + "/design-updater"} className="ant-btn">Update now</NavLink>}
      </>}
      {isUnverifiedToastUser && 
      <a href={`${env.timberContextPath}/design?size=menu-letter&open=integration`} className="ant-btn">Get Verified!</a>}
    </NavLink>
  );
}
